import type { ConnectionStatus, McpServerConnection } from '../types';

function statusClass(status: ConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'mcp-v2__pill mcp-v2__pill--connected';
    case 'failed':
      return 'mcp-v2__pill mcp-v2__pill--failed';
    case 'testing':
      return 'mcp-v2__pill mcp-v2__pill--testing';
    default:
      return 'mcp-v2__pill mcp-v2__pill--never';
  }
}

function statusLabel(status: ConnectionStatus): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'failed':
      return 'Failed';
    case 'testing':
      return 'Testing…';
    default:
      return 'Not tested';
  }
}

type Props = {
  connections: McpServerConnection[];
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleActive: (id: string) => void;
};

export default function ConnectionsList({
  connections,
  onOpen,
  onEdit,
  onDelete,
  onToggleActive,
}: Props) {
  if (connections.length === 0) {
    return (
      <div className="mcp-v2__panel">
        <div className="mcp-v2__empty">
          <h3>No connections yet</h3>
          <p>Add a custom MCP server or download one from the catalog.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mcp-v2__panel">
      <table className="mcp-v2__table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Transport</th>
            <th>Status</th>
            <th>Tools</th>
            <th>Resources</th>
            <th>Active</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {connections.map((c) => {
            const enabledTools = c.tools.filter((t) => t.enabled).length;
            const enabledResources = c.resources.filter((r) => r.enabled).length;
            return (
              <tr key={c.id} onClick={() => onOpen(c.id)}>
                <td>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  {c.description ? (
                    <div style={{ fontSize: '0.75rem', color: '#71717a', marginTop: 2 }}>
                      {c.description}
                    </div>
                  ) : null}
                  <div className="mcp-v2__meta" style={{ marginTop: 4 }}>
                    <span className="mcp-v2__tag">{c.source}</span>
                    {c.tags.slice(0, 3).map((t) => (
                      <span key={t} className="mcp-v2__tag">
                        {t}
                      </span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className="mcp-v2__pill mcp-v2__pill--transport">{c.transport}</span>
                </td>
                <td>
                  <span className={statusClass(c.status)}>{statusLabel(c.status)}</span>
                </td>
                <td>
                  {c.tools.length === 0
                    ? '—'
                    : `${enabledTools}/${c.tools.length}`}
                </td>
                <td>
                  {c.resources.length === 0
                    ? '—'
                    : `${enabledResources}/${c.resources.length}`}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="mcp-v2__switch"
                    data-on={c.isActive ? 'true' : 'false'}
                    aria-label={c.isActive ? 'Deactivate' : 'Activate'}
                    onClick={() => onToggleActive(c.id)}
                  />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="mcp-v2__actions">
                    <button type="button" className="mcp-v2__btn mcp-v2__btn--ghost" onClick={() => onOpen(c.id)}>
                      Open
                    </button>
                    <button type="button" className="mcp-v2__btn mcp-v2__btn--ghost" onClick={() => onEdit(c.id)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="mcp-v2__btn mcp-v2__btn--danger"
                      onClick={() => onDelete(c.id)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
